import * as React from 'react';
import { inputProps, pickerSuggestionProps } from './CalendarFilter.resources';
import { ReduxContext } from '@employee-experience/common/lib/ReduxContext';
import { TARReducerName } from '../../TAR.resources';
import IAppState from '../../../../App.types';
import * as Styled from './EmployeeFilter.styled';

import { IInformationRequest, IInformationState } from '../Information/Information.types';
import { requestInformation } from '../Information/Information.actions';
import { ITimeEntryListRequest } from '../TimeEntryList/TimeEntryList.types';
import { setDirtyState } from '../../DirtyCheck/Dirty.actions';
import { ComponentContext } from '@employee-experience/common/lib/ComponentContext';

import { CustomPicker } from './CustomPicker';
import { INITIAL_INFORMATION } from '../Information/Information.reducer';
import { IHttpClientResult } from '@employee-experience/common';
import { IBackendJobStatusState } from '../../../../Shared/JobStatus/BackendJobStatus.types';
import { AdminRoleType } from '../Information/information.resources';
import { IAdminActionTypesRedirectionState } from '../../Administration/AdminActionTypes/AdminActionTypes.types';
import { IEmployeeFilterResponse } from './EmployeeFilter.types';
import { Stack } from '@fluentui/react';
import { IPersonaProps } from '@fluentui/react/lib/Persona';
import { setEmployeeFilterSelectedItem } from './EmployeeFilter.actions';
import axios from 'axios';
import { getWeek } from '../Information/Information.saga';
import { css, IButtonProps } from '@fluentui/react';
import { HeaderActionsStyles, HeaderSearchBoxStyles } from './EmployeeFilter.styled';
import { CoherenceTheme } from '@cseo/styles';
const CancelToken = axios.CancelToken;
let source = CancelToken.source();
export function EmployeeFilter(): JSX.Element {
    const { useSelector, dispatch } = React.useContext(ReduxContext);
    const { authClient, httpClient, graphClient } = React.useContext(ComponentContext);
    const pickerRef = React.useRef<CustomPicker>(null);
    const url = __API_ENDPOINT__ + `employees/search`;

    const dispatchFetchInformation = React.useCallback(
        (data: IInformationRequest) => {
            dispatch(requestInformation(data));
        },
        [dispatch]
    );

    const info: IInformationState = useSelector((state: IAppState) => {
        const tar = state.dynamic[TARReducerName];
        return tar ? tar.information : INITIAL_INFORMATION;
    });

    const selectedItems: IPersonaProps[] = useSelector((state: IAppState) => {
        const tar = state.dynamic[TARReducerName];
        return tar ? tar.timeEntry.employeeFilter.selectedItems : [];
    });

    const onResolveSuggestions = async (filter: string): Promise<IPersonaProps[]> => {
        if (typeof source != typeof undefined) {
            source.cancel('Operation canceled due to new request.');
        }
        source = axios.CancelToken.source();
        let dataMap: IPersonaProps[] = [];
        if (!filter) {
            return [];
        }
        try {
            const { data: data }: IHttpClientResult<IEmployeeFilterResponse[]> = await httpClient.request<
                IEmployeeFilterResponse[]
            >({
                url: url + '?searchText=' + filter,
                method: 'get',
                resource: __CLIENT_ID__,
                cancelToken: source.token
            });

            dataMap = data.map(x => {
                return {
                    text: x.fullName,
                    tertiaryText: x.personnelNbr.toString(),
                    secondaryText: x.emailName
                        ? x.personnelNbr.toString() + ' | ' + x.emailName
                        : x.personnelNbr.toString(),
                    imageUrl: ``
                };
            });

            const getPhotos = async (): Promise<(string | null)[]> => {
                return Promise.all(
                    dataMap.map(item =>
                        item && item.secondaryText && item.secondaryText.split('|')[1]
                            ? graphClient.getPhoto(
                                  item.secondaryText
                                      .split('|')[1]
                                      .trim()
                                      .toLowerCase() + '@microsoft.com'
                              )
                            : null
                    )
                );
            };

            getPhotos().then(photo => {
                const result = dataMap.map((x, index) => {
                    x.imageUrl = photo ? photo[index] || '' : '';
                    return x;
                });
                pickerRef.current ? pickerRef.current.refreshSuggestions(result) : null;
            });
        } catch (error) {}

        return dataMap;
    };

    const getTextFromItem = (item: IPersonaProps): string => {
        return item.text || '';
    };

    const dirtyFlag: boolean = useSelector((state: IAppState) => {
        const tar = state.dynamic[TARReducerName];
        const timeEntryList = tar && tar.timeEntry && tar.timeEntry.timeEntryList;
        return timeEntryList
            ? timeEntryList.find(x => x.weekDayDetails.find(d => d.dirty === true)) !== undefined
            : false;
    });

    const jobStatus: IBackendJobStatusState = useSelector((state: IAppState) => {
        const tar = state.dynamic[TARReducerName];
        return tar ? tar.backendJobStatus : { isJobRunning: false };
    });

    const redirection: IAdminActionTypesRedirectionState | undefined = useSelector((state: IAppState) => {
        const tar = state.dynamic[TARReducerName];
        return tar ? tar.adminActionTypes.redirection : undefined;
    });

    const onChange = async (items?: IPersonaProps[]): Promise<void> => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dateRange = getWeek(today);
        if (dirtyFlag === true) {
            const resolved = confirm('You have unsaved changes, do you want to continue?');
            if (resolved === true) {
                dispatch(setDirtyState(false));
                dispatch(setEmployeeFilterSelectedItem({ selectedItems: items || [] }));
            } else {
                return;
            }
        }
        const alias = await authClient.getUserId();
        if (items && items.length > 0 && items[0].tertiaryText) {
            const data: ITimeEntryListRequest = {
                start: dateRange[0],
                end: dateRange[dateRange.length - 1],
                date: today,
                alias:
                    items[0].secondaryText && items[0].secondaryText.split('|')[1]
                        ? items[0].secondaryText?.split('|')[1].trim()
                        : '',
                perner: parseInt(items[0].tertiaryText),
                authAlias: alias ? alias.split('@')[0] : ''
            };
            dispatchFetchInformation(data);
        } else {
            const data: ITimeEntryListRequest = {
                start: dateRange[0],
                end: dateRange[dateRange.length - 1],
                date: today,
                alias: alias ? alias.split('@')[0] : '',
                perner: undefined,
                authAlias: alias ? alias.split('@')[0] : ''
            };
            dispatchFetchInformation(data);
        }
        dispatch(setEmployeeFilterSelectedItem({ selectedItems: items || [] }));
    };
    const overflowProps: IButtonProps = {
        id: 'overflow-button-id',
        ariaLabel: 'Access search box. Select to search',
        className: css('ms-CommandBar-overflowButton', HeaderActionsStyles.rightItemButton),
        menuIconProps: {
            iconName: 'Search',
            style: { backgroundColor: 'transparent !important' }
        },
        styles: {
            menuIcon: {
                color: CoherenceTheme.palette.white + ' !important',
                backgroundColor: 'transparent !important',
                fontSize: '16px'
            }
        }
    };
    // only show in tar page
    if (
        info.isAdmin &&
        window.location.href.lastIndexOf('tar') === window.location.href.length - 3 &&
        jobStatus.isJobRunning === false &&
        info.adminRoleTypeCode !== AdminRoleType.TARHRAdmin &&
        (redirection === undefined || redirection.redirectionFlag === false)
    ) {
        return (
            <Styled.SearchCommandBar
                overflowButtonProps={overflowProps}
                items={[
                    {
                        key: 'SearchBox',
                        onRender: function search(): React.ReactNode {
                            return (
                                <Stack horizontal style={{ overflow: 'auto', alignSelf: 'center' }} role="menuitem">
                                    <Styled.AdminSearchNormalPeoplePicker
                                        role="searchbox"
                                        id="AdminSearchNormalPeoplePicker"
                                        className={HeaderSearchBoxStyles.searchBox}
                                        componentRef={pickerRef}
                                        inputProps={inputProps}
                                        pickerSuggestionsProps={pickerSuggestionProps}
                                        resolveDelay={450}
                                        itemLimit={1}
                                        onResolveSuggestions={onResolveSuggestions}
                                        getTextFromItem={getTextFromItem}
                                        onChange={onChange}
                                        selectedItems={selectedItems}
                                        removeButtonAriaLabel={'Close'}
                                        selectionAriaLabel={'Selected Employee'}
                                    />
                                </Stack>
                            );
                        }
                    }
                ]}
            />
        );
    }
    return <></>;
}
